import { Box, VStack } from "@chakra-ui/react"
import HTMLText from "@pathwright/ux/src/components/text/HTMLText"
import { useMemo } from "react"
import { useCoverPageContext } from "./CoverPageProvider"
import { useCoverPageIsEditable } from "./access"
import { COVER_PAGE_PADDING } from "./constants"
import CoverPageBlankSlate, {
  useCoverPageBlankSlateText
} from "./design/CoverPageBlankSlate"
import { useCoverPageBlankSlate } from "./design/useCoverPageBlankSlate"
import { requestCoverPageInlineSectionEdit } from "./design/CoverPageInlineSectionEditor"
import CoverPageInlineSectionEditor from "./design/CoverPageInlineSectionEditor"
import { CoverPagePanelSection } from "./types"
import { useCoverPageTranslate } from "./utils"

const CoverPageDescriptionEdit = () => {
  const { t } = useCoverPageTranslate()
  const missingOverview = useCoverPageBlankSlate("overview")
  const { hint: overviewHint } = useCoverPageBlankSlateText("overview")
  const resource = useCoverPageContext((v) => v.resource)
  const description = useMemo(() => resource?.description || "", [resource])
  const updateCoverPageResourceDetails = useCoverPageContext(
    (v) => v.updateCoverPageResourceDetails
  )
  const refetchCoverPageResource = useCoverPageContext(
    (v) => v.refetchCoverPageResource
  )

  if (!resource) return null

  return (
    <CoverPageInlineSectionEditor
      sectionKey={CoverPagePanelSection.Description}
      value={description}
      blankSlate={
        missingOverview ? (
          <CoverPageBlankSlate
            kind="overview"
            onClick={() =>
              requestCoverPageInlineSectionEdit(
                CoverPagePanelSection.Description
              )
            }
          />
        ) : undefined
      }
      placeholder={overviewHint}
      triggerLabel={t("description.overview")}
      onSave={async (descriptionDraft) => {
        if (descriptionDraft === description) return

        try {
          await updateCoverPageResourceDetails({
            description: descriptionDraft
          })
          await refetchCoverPageResource()
        } catch (error) {
          console.error("Error updating cover page description:", error)
          throw error
        }
      }}
    />
  )
}

const CoverPageDescription = () => {
  const resource = useCoverPageContext((v) => v.resource)
  const descriptionSectionRef = useCoverPageContext(
    (v) => v.descriptionSectionRef
  )
  const description = useMemo(() => resource?.description || "", [resource])

  const isImmersive = useCoverPageContext((v) => v.immersiveActive)
  const isEditable = useCoverPageIsEditable()

  // We can render the editor without a description, if editing is possible
  if (!isEditable && !description) return null

  return (
    <Box
      id="description"
      className="CoverPageDescription"
      ref={descriptionSectionRef}
      position="relative"
      role="group"
      w="100%"
      px={COVER_PAGE_PADDING}
      pt={"30px"}
      css={{
        "& ul, & ol": {
          paddingLeft: "2em",
          marginBottom: "15px"
        },
        "& ul": {
          listStyleType: "disc"
        },
        "& p": {
          margin: "0 0 0.75em"
        },
        "& .ProseMirror": {
          paddingBlock: "0",
          paddingBottom: "16px"
        }
      }}
    >
      {isEditable && !isImmersive ? (
        <CoverPageDescriptionEdit />
      ) : (
        <HTMLText html={description} color="PRIMARY_GRAY" w="100%" />
      )}
    </Box>
  )
}

export const CoverPageOverviewImmersive = () => {
  return (
    <VStack
      w="full"
      bgColor="backgroundSecondary"
      overflowY="auto"
      borderRadius="card"
      maxHeight="100%"
      maxWidth={{ base: "100%", md: "800px" }}
      margin="0 auto"
      css={{
        "& .CoverPageDescription": {
          paddingBottom: "20px",
          paddingTop: "20px"
        }
      }}
    >
      <CoverPageDescription />
    </VStack>
  )
}

export default CoverPageDescription
